import type { Element } from 'react';
import React from 'react';
import { useSelector } from 'react-redux';
import { classnames } from 'Utils/classnames/classnames.utils';
import { inRange, without } from 'lodash';
import moment from 'moment';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
import { Text } from 'Components/frameworks/text/Text';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import {
  bannersSelector,
  filteredProductsSeasonsSelector,
  getFacetingProductIds,
} from 'Store/faceting/faceting.selector';
import { getDepartureDate } from 'Store/trip-criteria/tripCriteria.selector';
import { getKids } from 'Store/trip-information/storeHelper';
import type { Kids } from 'Store/trip-information/kids/kids.reducer';
import 'Store/trip-information/kids/kids.reducer';
import { themes } from './SearchBanners.themes';

const getResponsiveClasses = (screenSizes: Array<'xs' | 'sm' | 'md' | 'lg' | 'xl'>) => {
  const classes = [];

  if (screenSizes.length === 0) {
    return classes;
  }

  if (!screenSizes.includes('xs')) {
    classes.push('hidden');
  }

  classes.push(screenSizes.includes('sm') ? 'sm:flex' : 'sm:hidden');
  classes.push(screenSizes.includes('md') ? 'md:flex' : 'md:hidden');
  classes.push(screenSizes.includes('lg') ? 'lg:flex' : 'lg:hidden');
  classes.push(screenSizes.includes('xl') ? 'xl:flex' : 'xl:hidden');
  return classes;
};

const filterByPosition = ({ position: { type, value } }: PertimmBanner, position: string) =>
  type === position || value === position;

const filterBySeason = ({ content: { filters = [] } }: PertimmBanner, seasons: Array<string>) => {
  const productsHaveSeasons = seasons && seasons.length > 0;
  const bannerHasSeasonFilter = filters && filters.seasons && filters.seasons.length > 0;

  if (!productsHaveSeasons || !bannerHasSeasonFilter) {
    return true;
  }

  return filters.seasons.some(
    (filterSeasons) => seasons.sort().join() === filterSeasons.sort().join(),
  );
};

const filterByKidsAge = (
  { content: { filters = [] } }: PertimmBanner,
  kids: Array<Kids>,
  departureDate: number | null | undefined,
) => {
  const searchHasKids = kids && kids.length > 0 && kids.some((kid) => !!kid.birthdate);
  const bannerHasKidsAgeFilter =
    filters && filters.kidsAgeInMonths && filters.kidsAgeInMonths.length > 0;

  if (!bannerHasKidsAgeFilter) {
    return true;
  }

  if ((!searchHasKids || !departureDate) && bannerHasKidsAgeFilter) {
    return false;
  }

  return kids.some((kid) => {
    const ageInMonths = moment(departureDate).diff(moment(kid.birthdate), 'months');
    return filters.kidsAgeInMonths.some((ageFilter) => {
      const [min, max] = ageFilter.split('-');
      return inRange(ageInMonths, parseInt(min, 10), parseInt(max, 10));
    });
  });
};

const filterByProductIds = (
  { content: { filters = [] } }: PertimmBanner,
  productIds: Array<string>,
) => {
  const searchHasProductIds = productIds && productIds.length > 0;
  const bannerHasProductIdsFilter = filters && filters.products && filters.products.length > 0;

  if (!bannerHasProductIdsFilter) {
    return true;
  }

  if (!searchHasProductIds && bannerHasProductIdsFilter) {
    return false;
  }

  return without(productIds, ...filters.products).length === 0;
};

const WithSeeMore = ({ children, className }) => {
  const { isDesktop } = useResponsive();
  return (
    <SeeMore
      className={className}
      isCollapseEnabled={!isDesktop}
      seeLessLabel={t('common.loadLess')}
      seeMoreLabel={t('common.loadMore')}
    >
      {children}
    </SeeMore>
  );
};

type Props = {
  position?: string;
  className?: string;
};
export const SearchBanners = ({ position, className }: Props) => {
  const bannerContents: Array<PertimmBanner> = useSelector((state) => {
    const seasons = filteredProductsSeasonsSelector(state);
    const kids = getKids(state);
    const departureDate = getDepartureDate(state);
    const banners = bannersSelector(state);
    const productIds = getFacetingProductIds(state);

    if (!position) {
      return banners;
    }

    return banners
      .filter((banner) => filterByPosition(banner, position))
      .filter((banner) => filterByProductIds(banner, productIds))
      .filter((banner) => filterByKidsAge(banner, kids, departureDate))
      .filter((banner) => filterBySeason(banner, seasons));
  });

  if (!bannerContents || bannerContents.length === 0) {
    return null;
  }

  const { thRoot, thBannerRoot, thBannerHtml, thBannerImg, thBannerText } =
    themes[position || 'default']();
  return (
    <SiteContainer>
      <div className={classnames(thRoot, className)}>
        {bannerContents.map<Element<'div'>>(({ content }) => (
          <div
            className={classnames(
              thBannerRoot,
              content.screenSizes ? getResponsiveClasses(content.screenSizes) : '',
            )}
            key={content.text}
          >
            {content.type === 'img' && (
              <img className={thBannerImg} src={content.imgUrl} alt={content.text} />
            )}
            {content.type === 'text' && (
              <div>
                <WithSeeMore className={thBannerText}>
                  <Text text={content.text} />
                </WithSeeMore>
              </div>
            )}
            {content.type === 'html' && (
              <div>
                <WithSeeMore className={thBannerHtml}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: content.text,
                    }}
                  />
                </WithSeeMore>
              </div>
            )}
          </div>
        ))}
      </div>
    </SiteContainer>
  );
};
